import { Injectable } from '@angular/core';
import { WilsonState } from '@wilson/non-domain-specific/decorators/wilson-state';
import {
  debounceTime,
  filter,
  map,
  Observable,
  pairwise,
  shareReplay,
} from 'rxjs';
import { ShiftId, UserId } from '../interfaces';
import { AssignmentStack, GroupedUserDataV2 } from '@wilson/interfaces';

@Injectable({
  providedIn: 'root',
})
export class AssignedRegionVisibilityStateService {
  @WilsonState<Record<UserId, Set<ShiftId>>>({})
  private visibleShiftIdsByUserIdMap!: Record<UserId, Set<ShiftId>>;
  visibleShiftIdsByUserIdMap$!: Observable<Record<UserId, Set<ShiftId>>>;

  public visibleShiftIdsSet$: Observable<Set<ShiftId>> =
    this.visibleShiftIdsByUserIdMap$.pipe(
      debounceTime(1),
      map((map) => {
        const set = new Set<ShiftId>();
        Object.values(map).forEach((value) => {
          value.forEach((shiftId) => {
            set.add(shiftId);
          });
        });
        return set;
      }),
      shareReplay(1),
    );

  public newlyVisibleShiftIdsSet$: Observable<{
    new: Set<ShiftId>;
    previouslyVisibleShiftIds: Set<ShiftId>;
  }> = this.visibleShiftIdsSet$.pipe(
    pairwise(),
    map(([prev, curr]) => {
      const result = {
        new: new Set<ShiftId>(),
        previouslyVisibleShiftIds: new Set<ShiftId>(),
      };
      curr.forEach((id) => {
        if (!prev.has(id)) {
          result.new.add(id);
        } else {
          result.previouslyVisibleShiftIds.add(id);
        }
      });
      return result;
    }),
    filter((result) => result.new.size > 0),
    shareReplay(1),
  );

  @WilsonState(new Set())
  private visibleUserIdsSet!: Set<UserId>;
  visibleUserIdsSet$!: Observable<Set<UserId>>;

  updateUserVisibility(userIds: Set<UserId>): void {
    this.visibleUserIdsSet = userIds;
  }

  /**
   * Snapshot of the shift IDs currently rendered for one user's row. Used after a shift/absence/
   * activity mutation to resend validation for everything already on screen for the affected
   * user(s), rather than just the one shift that changed - see
   * `TimelineCommonOperationsService.revalidateAffectedUsers`.
   */
  getVisibleShiftIdsForUser(userId: UserId): Set<ShiftId> {
    return this.visibleShiftIdsByUserIdMap[userId] ?? new Set();
  }

  updateShiftVisibility(
    isVisible: boolean,
    shiftId: ShiftId,
    userId: UserId,
  ): void {
    const newMap = { ...this.visibleShiftIdsByUserIdMap };

    if (isVisible) {
      if (!(userId in newMap) || !newMap[userId].has(shiftId)) {
        newMap[userId] = new Set(newMap[userId] || []);
        newMap[userId].add(shiftId);
        this.visibleShiftIdsByUserIdMap = newMap;
      }
    } else {
      if (userId in newMap && newMap[userId].has(shiftId)) {
        newMap[userId] = new Set(newMap[userId]);
        newMap[userId].delete(shiftId);
        this.visibleShiftIdsByUserIdMap = newMap;
      }
    }
  }

  updateCellViewTimelineShiftsVisibilityForUser(
    newAssignments: AssignmentStack[],
    previousAssignments: AssignmentStack[],
    userId: UserId,
  ): void {
    const newMap = { ...this.visibleShiftIdsByUserIdMap };

    if (previousAssignments.length > 0) {
      previousAssignments.forEach((assignmentStack) => {
        assignmentStack.shifts.forEach((shift) => {
          newMap[userId]?.delete(shift.id);
        });
      });
    }

    if (newAssignments.length > 0) {
      newAssignments.forEach((assignmentStack) => {
        assignmentStack.shifts.forEach((shift) => {
          if (!(userId in newMap)) {
            newMap[userId] = new Set();
          }
          newMap[userId]?.add(shift.id);
        });
      });
    }

    this.visibleShiftIdsByUserIdMap = newMap;
  }

  updateRegularTimelineShiftsVisibilityForUser(
    newAssignments: GroupedUserDataV2[],
    previousAssignments: GroupedUserDataV2[],
    userId: UserId,
  ): void {
    const newMap = { ...this.visibleShiftIdsByUserIdMap };

    if (previousAssignments.length > 0) {
      previousAssignments.forEach((userDataRow) => {
        userDataRow.shifts.forEach((shiftView) => {
          newMap[userId]?.delete(shiftView.shift.id);
        });
      });
    }
    if (newAssignments.length > 0) {
      newAssignments.forEach((userDataRow, index) => {
        if (index < 2) {
          userDataRow.shifts.forEach((shiftView) => {
            if (!(userId in newMap)) {
              newMap[userId] = new Set();
            }
            newMap[userId].add(shiftView.shift.id);
          });
        }
      });
    }

    this.visibleShiftIdsByUserIdMap = newMap;
  }

  clearAllShiftVisibilityRecord(): void {
    this.visibleShiftIdsByUserIdMap = {};
  }

  clearAllShiftVisibilityRecordForUser(userId: UserId): void {
    const newMap = { ...this.visibleShiftIdsByUserIdMap };

    delete newMap[userId];

    this.visibleShiftIdsByUserIdMap = newMap;
  }
}
